import { useState, useEffect, useMemo, useRef } from "react";
import { createFilterOptions, getValidId, isValidId, updateActiveTask, Util } from '../../util/Util'
import { Item } from '../../util/Item'

import RefreshButton from "../../components/RefreshButton";
import { SelectInstance } from "react-select";
import { useConversion } from "./ConversionContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useUI } from "../../providers/MoleUIContext";
import { useAuth } from "../../hooks/useAuth";
import { MYSQL_LIMITS } from "../../util/constants";
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, qtyMin } from "../../util/quantity";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { Alert, Autocomplete, Grid, TextField, Typography } from "@mui/material";
import QuantityStepperInput from "../../components/QuantityStepperInput";
import { focusAndSelect } from "../../util/focusField";
import AsynchronousPrice from "../../components/PriceAutoComplete";
import { renderCatalogItemOption } from "../../components/itemAutocompleteRenderOption";
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from "../../components/catalogItemAutocomplete";
import { applySavedCatalogOption, CatalogItemPickerProvider } from "../../components/CatalogItemActions";
import type { CatalogItemRow } from "../inventory/ItemDetailModal";
import {
	isItemMetered,
	isNegativeStockAllowed,
	shouldEnforceStockQuantity,
} from "../../components/quantityStepperStock";
import {
	CONVERSION_FACTOR_MAX,
	effectiveFactor,
	getConversionBlockers,
	inputQtyFromStored,
	maxInputQty,
	quantitiesFromInput,
	type ConversionBlocker,
} from "../../util/conversionMath";
import VirtualizedListbox from "../../components/VirtualizedListbox";

interface Props {
	bundle?: any;
	setValidateCheck: (value: boolean) => void;
	setBlockers?: (blockers: ConversionBlocker[]) => void;
}

const itemAutocompleteSx = {
	'& .MuiAutocomplete-listbox': {
		padding: 0,
	},
	'& .MuiAutocomplete-option': {
		padding: '4px 8px',
		fontSize: '0.875rem',
		minHeight: 'auto',
	},
	'& .MuiAutocomplete-popupIndicator': {
		padding: '4px',
	},
};

interface OptionType {
	value: number;
	label: string;
}

const ConversionItemDetail = ({ bundle, setValidateCheck, setBlockers }: Props) => {
	const [barcodes, setBarcodes] = useState<any>({});
	const [sourceBarcode, setSourceBarcode] = useState("");
	const [destBarcode, setDestBarcode] = useState("");
	const [selectedSourceItem, setSelectedSourceItem] = useState<Item | null>(null);
	const [selectedDestItem, setSelectedDestItem] = useState<Item | null>(null);
	const [errorInput, setErrorInput] = useState("");
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [isSourcePriceLoading, setIsSourcePriceLoading] = useState(false);
	const [isDestPriceLoading, setIsDestPriceLoading] = useState(false);
	const [sourcePrices, setSourcePrices] = useState<any[]>([]);
	const [destPrices, setDestPrices] = useState<any[]>([]);
	const [useSourceBarcode, setUseSourceBarcode] = useState(false);
	const [useDestBarcode, setUseDestBarcode] = useState(false);

	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { pendingTasks, setPendingTasks, activeTask, items, setItems, sourceStock, destStock, finalQuantity } = useConversion();
	const { api } = useAuth();

	const sourceBarcodeRef = useRef<HTMLInputElement>(null);
	const destBarcodeRef = useRef<HTMLInputElement>(null);
	const sourceItemRef = useRef<SelectInstance<OptionType, false>>(null);
	const destItemRef = useRef<SelectInstance<OptionType, false>>(null);
	const quantityRef = useRef<HTMLInputElement>(null);
	const amountRef = useRef<HTMLInputElement>(null);
	const prevSourceRef = useRef<number | null>(null);
	const prevTaskIdRef = useRef<string | null>(null);

	const errorMessageRef = useRef<string>("");
	const isFirstRender = useRef(true);

	const sourceCatalogItem = items.find(
		(catalogEntry: any) => Number(catalogEntry.id) === Number(activeTask?.source_item),
	);
	const isSourceMetered = isItemMetered(sourceCatalogItem, true);
	const allowNegativeStock = isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value);
	const enforceSourceStock = shouldEnforceStockQuantity({
		isMetered: isSourceMetered,
		allowNegativeStock,
	});
	const factor = effectiveFactor(activeTask?.convType, activeTask?.factor || 1);
	const inputQty = inputQtyFromStored(
		activeTask?.convType,
		Number(activeTask?.quantity) || 0,
		Number(activeTask?.produce_qty) || 0,
		factor,
	);
	const { sourceQty, produceQty } = quantitiesFromInput(activeTask?.convType, inputQty, factor);
	const destCatalogItem = items.find(
		(catalogEntry: any) => Number(catalogEntry.id) === Number(activeTask?.dest_item),
	);
	const sourceFractional = selectedSourceItem?.isFractional === true || sourceCatalogItem?.IsFractional === true;
	const destFractional = selectedDestItem?.isFractional === true || destCatalogItem?.IsFractional === true;
	const inputFractional = activeTask?.convType === "DE-KIT" ? sourceFractional : destFractional;
	const stockCappedMax = maxInputQty(
		activeTask?.convType,
		sourceStock,
		factor,
		QTY_LIMITS.max,
		inputFractional,
	);
	const quantityMax = enforceSourceStock
		? Math.min(QTY_LIMITS.max, Math.max(stockCappedMax, qtyMin(inputFractional)))
		: QTY_LIMITS.max;
	const sourceExceedsStock =
		enforceSourceStock
		&& Number(activeTask?.source_item) > 0
		&& Number.isFinite(sourceQty)
		&& sourceQty > sourceStock;
	const barcodeEnabled = Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true);

	const applyInputQty = (nextInput: number, nextFactor = factor, nextType = activeTask?.convType) => {
		const resolvedFactor = effectiveFactor(nextType, nextFactor);
		let n = nextInput;
		if (enforceSourceStock && nextType) {
			const cap = maxInputQty(nextType, sourceStock, resolvedFactor, QTY_LIMITS.max, inputFractional);
			if (Number.isFinite(cap) && cap > 0) {
				n = Math.min(n, cap);
			}
		}
		const next = quantitiesFromInput(nextType, n, resolvedFactor);
		const inputOk = nextType === "DE-KIT"
			? Number.isFinite(next.sourceQty) && next.sourceQty > 0 && (sourceFractional || isWholeNumber(next.sourceQty))
			: Number.isFinite(next.produceQty) && next.produceQty > 0 && (destFractional || isWholeNumber(next.produceQty));
		updateActiveTask(setPendingTasks, {
			produce_qty: inputOk && Number.isFinite(next.produceQty) && next.produceQty > 0 ? next.produceQty : 0,
			factor: resolvedFactor,
			quantity: inputOk && Number.isFinite(next.sourceQty) && next.sourceQty > 0 ? next.sourceQty : 0,
		});
	};

	const defaultOptions: OptionType[] = items.map((item) => ({
		value: item.id,
		label: item.ItemName,
	}));

	/**
	 * Source consumes real stock, so it must already be in the depot.
	 * Destination stays catalog-wide (`items`) — the first conversion into a new
	 * item is exactly the case that creates its depot row.
	 */
	const depotItems = useMemo(
		() => items.filter((item: any) => item.InDepot !== false),
		[items],
	);

	/** A destination with no depot row yet has no stock figure worth showing. */
	const destInDepot =
		items.find((item: any) => Number(item.id) === Number(activeTask?.dest_item))?.InDepot !== false;

	const fetchItems = async () => {
		if ((activeTask?.depot || -1) < 1) return;

		setIsItemLoading(true);

		// IncludeCatalog: the result item may never have been produced into this
		// depot before, so it has no depot row yet. Source is narrowed below.
		const res = await api.get(`depots/${activeTask?.depot}/items`, {
			params: {
				IncludeCatalog: true,
			}
		});

		if (res) {
			if (res.status === 200) {
				setItems(res.data.items);
				setBarcodes(res.data.barcodes);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchItems);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsItemLoading(false);
	};

	const fetchSourcePrices = async () => {
		setSourcePrices([]);  // Clear previous prices
		if ((activeTask?.source_item || -1) < 1) return;

		setIsSourcePriceLoading(true);

		const res = await api.get(`items/${activeTask?.source_item}/price_history`);
		if (res) {
			if (res.status === 200) {
				setSourcePrices(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchSourcePrices);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsSourcePriceLoading(false);
	};

	const fetchDestPrices = async () => {
		setDestPrices([]);  // Clear previous prices
		if ((activeTask?.dest_item || -1) < 1) return;

		setIsDestPriceLoading(true);

		const res = await api.get(`items/${activeTask?.dest_item}/price_history`);

		if (res) {
			if (res.status === 200) {
				setDestPrices(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchDestPrices);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsDestPriceLoading(false);
	};


	const handleCatalogSaved = (updated: CatalogItemRow) => {
		setItems((prev) => applySavedCatalogOption(prev, updated));
		setSelectedSourceItem((prev) =>
			prev && prev.id === updated.id
				? new Item({ ...prev, item: updated.ItemName || prev.item, item_code: updated.ItemCode || prev.item_code, isFractional: updated.IsFractional === true })
				: prev
		);
		setSelectedDestItem((prev) =>
			prev && prev.id === updated.id
				? new Item({ ...prev, item: updated.ItemName || prev.item, item_code: updated.ItemCode || prev.item_code, isFractional: updated.IsFractional === true })
				: prev
		);
		updateActiveTask(setPendingTasks, (prev) => {
			const next = { ...prev };
			if (Number(prev.source_item) === Number(updated.id)) {
				next.source_item_desc = updated.ItemName || prev.source_item_desc;
			}
			if (Number(prev.dest_item) === Number(updated.id)) {
				next.dest_item_desc = updated.ItemName || prev.dest_item_desc;
			}
			return next;
		});
	};

	const onSourceItemChanged = (item_id: number) => {
		const item: any = depotItems.find((item: any) => item.id === item_id);

		if (item) {
			const myItem = new Item({
				id: item.id,
				item: item.ItemName,
				price: item.CostPrice,
				stock: item.Stock,
				quantity: 1,
				isFractional: item.IsFractional === true,
			});

			setSelectedSourceItem(myItem);
		} else {
			setSelectedSourceItem(null);
		}
	};

	const lookupSourceBarcode = (code: string = sourceBarcode) => {
		const itemID = barcodes[code];

		const scanned = depotItems.find((item: any) => item.id === itemID);

		if (itemID && scanned) {
			updateActiveTask(setPendingTasks, { source_item: itemID, source_item_desc: scanned.ItemName || "" });
			focusAndSelect(quantityRef);
		} else {
			sourceItemRef.current?.focus();
			setErrorInput("sourceBarcodeInput");
			setTimeout(() => setErrorInput(""), 2000);
		}
	}

	const onDestItemChanged = (item_id: number) => {
		const item: any = items.find((item: any) => item.id === item_id);

		if (item) {
			const myItem = new Item({
				id: item.id,
				item: item.ItemName,
				price: item.CostPrice,
				stock: item.Stock,
				quantity: 1,
				isFractional: item.IsFractional === true,
			});

			setSelectedDestItem(myItem);
		} else {
			setSelectedDestItem(null);
		}
	};

	const lookupDestBarcode = (code: string = destBarcode) => {
		const itemID = barcodes[code];

		if (itemID) {
			const item_desc = items.find((item: any) => item.id === itemID)?.ItemName || "";
			updateActiveTask(setPendingTasks, { dest_item: itemID, dest_item_desc: item_desc });
			focusAndSelect(quantityRef);
		} else {
			destItemRef.current?.focus();
			setErrorInput("destBarcodeInput");
			setTimeout(() => setErrorInput(""), 2000);
		}
	}

	const validateQty = (value: number): boolean => {
		let error = false;
		const next = quantitiesFromInput(activeTask?.convType, value, factor);
		const needed = Number.isFinite(next.sourceQty) ? next.sourceQty : value;

		if (value < qtyMin(inputFractional) || value > QTY_LIMITS.max) {
			error = true;
			errorMessageRef.current = t("common:validation.quantityRange", { max: Util.formatNumber(QTY_LIMITS.max) });
		} else if (!inputFractional && !isWholeNumber(value)) {
			error = true;
			errorMessageRef.current = t("common:validation.quantityMustBeWhole");
		} else if (enforceSourceStock && needed > sourceStock) {
			error = true;
			errorMessageRef.current = t("common:conversion.blockers.insufficientStock", {
				required: Util.formatNumber(needed),
				available: Util.formatNumber(sourceStock),
			});
		}

		if (error) {
			setErrorInput("quantityInput");
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 2000);
		}

		return !error;
	}

	const validateFctr = (value: number): boolean => {
		if (value < 1 || value > CONVERSION_FACTOR_MAX) {
			errorMessageRef.current = t("common:validation.factorRange", { max: Util.formatNumber(CONVERSION_FACTOR_MAX) });

			setErrorInput("factorInput");
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 2000);

			return false;
		}
		return true;
	}

	const validateAmt = (value: number): boolean => {
		if (isNaN(value) || value < 0 || value > MONEY_LIMITS.max) {
			setErrorInput("amountInput");
			setTimeout(() => setErrorInput(""), 500);
			return false;
		}
		return true;
	}

	useEffect(() => {
		if (selectedSourceItem) {
			updateActiveTask(setPendingTasks, { source_cost: selectedSourceItem.price });
		}
	}, [selectedSourceItem]);

	useEffect(() => {
		if (selectedDestItem) {
			updateActiveTask(setPendingTasks, { dest_cost: selectedDestItem.price });
		}
	}, [selectedDestItem]);

	useEffect(() => {
		if (activeTask) {
			const currentTaskId = activeTask.id;
			if (prevTaskIdRef.current) {
				if (prevTaskIdRef.current !== currentTaskId) {
					fetchItems();
				} else {
					const currentSource = activeTask.depot;
					if (prevSourceRef.current !== currentSource) {
						fetchItems();
					}
					prevSourceRef.current = currentSource;
				}
				prevTaskIdRef.current = currentTaskId;
			} else {
				prevTaskIdRef.current = currentTaskId;
			}
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask && !selectedSourceItem) {
			const catalog = items.find((entry: any) => Number(entry.id) === Number(activeTask.source_item));
			setSelectedSourceItem(new Item({
				id: activeTask.source_item,
				quantity: 1,
				price: activeTask.source_cost,
				isFractional: catalog?.IsFractional === true || catalog?.is_fractional === true,
			}));
		}
		if (activeTask && !selectedDestItem) {
			const catalog = items.find((entry: any) => Number(entry.id) === Number(activeTask.dest_item));
			setSelectedDestItem(new Item({
				id: activeTask.dest_item,
				quantity: produceQty,
				price: activeTask.dest_cost,
				isFractional: catalog?.IsFractional === true || catalog?.is_fractional === true,
			}));
		}
	}, [activeTask]);

	useEffect(() => {
		if (items.length > 0) {
			updateActiveTask(setPendingTasks, { source_item: getValidId(depotItems, activeTask?.source_item), source_item_desc: depotItems.find((i) => i.id === getValidId(depotItems, activeTask?.source_item))?.ItemName || "" });
			updateActiveTask(setPendingTasks, { dest_item: getValidId(items, activeTask?.dest_item), dest_item_desc: items.find((i) => i.id === getValidId(items, activeTask?.dest_item))?.ItemName || "" });
		}
	}, [items]);

	useEffect(() => {
		if (isFirstRender.current) return;

		if (useSourceBarcode) {
			sourceBarcodeRef.current?.focus();
		} else {
			sourceItemRef.current?.focus();
		}
	}, [useSourceBarcode]);

	useEffect(() => {
		if (isFirstRender.current) return;

		if (useDestBarcode) {
			destBarcodeRef.current?.focus();
		} else {
			destItemRef.current?.focus();
		}
	}, [useDestBarcode]);

	useEffect(() => {
		setUseSourceBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
		setUseDestBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
	}, []);



	/* Make sure it is the last effect */
	useEffect(() => {
		isFirstRender.current = false;
	}, []);

	useEffect(() => {
		const blockers = getConversionBlockers({
			convType: activeTask?.convType,
			sourceItem: activeTask?.source_item,
			destItem: activeTask?.dest_item,
			inputQty,
			factor,
			sourceCost: activeTask?.source_cost,
			destCost: activeTask?.dest_cost,
			sourceStock,
			enforceSourceStock,
			itemsReady: true,
			clientsReady: false,
			depotsReady: false,
			loading: isSourcePriceLoading || isDestPriceLoading || isItemLoading,
			unsignedMax: QTY_LIMITS.max,
			fractional: inputFractional,
		});
		setBlockers?.(blockers);
		setValidateCheck(blockers.length === 0);
	}, [activeTask, items, isSourcePriceLoading, isDestPriceLoading, isItemLoading, sourceStock, inputQty, factor, enforceSourceStock]);

	const sourceName = activeTask?.source_item_desc || t("common:conversion.sourceItem");
	const destName = activeTask?.dest_item_desc || t("common:conversion.destItem");
	const previewReady = Number(activeTask?.source_item) > 0 && Number(activeTask?.dest_item) > 0 && Number.isFinite(sourceQty) && sourceQty > 0;

	return (
		<form style={{ marginBottom: '20px' }}>
			<Grid container rowSpacing={0} columnSpacing={0}>
				{barcodeEnabled && (
				<Grid size={{ xs: 4, sm: 4, md: 4 }} >
					<TextField
						id="sourceBarcodeInput"
						size="small"
						label={t("common:ui.labels.barcode")}
						variant="filled"
						fullWidth
						type="text"
						value={sourceBarcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={sourceBarcodeRef}
						slotProps={{
							input: {
								endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupSourceBarcode()} />,
							}
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								lookupSourceBarcode(sourceBarcode);
							}
						}}
						onChange={(e) => setSourceBarcode(e.target.value)}
						disabled={!activeTask || isItemLoading || Object.keys(barcodes).length === 0}
						error={errorInput === "sourceBarcodeInput"}
						helperText={
							errorInput === "sourceBarcodeInput"
								? t("common:stockTrace.barcodeNotFound")
								: ""
						}
					/>
				</Grid>
				)}
				<Grid size={{ xs: barcodeEnabled ? 8 : 12, sm: barcodeEnabled ? 8 : 12, md: barcodeEnabled ? 8 : 12 }} >
					<CatalogItemPickerProvider onItemSaved={handleCatalogSaved}>
					<Autocomplete
						id="sourceItemSelect"
						size="small"
						options={depotItems}
						ListboxComponent={VirtualizedListbox}
						filterOptions={createFilterOptions((opt) => opt.ItemName || "")}
						{...catalogItemAutocompleteInteractionProps}
						getOptionLabel={(item) => item.ItemName || ""}
						isOptionEqualToValue={isCatalogItemOptionEqualToValue}
						renderOption={(props, option) => renderCatalogItemOption(props, option, "both")}
						value={depotItems.find((i) => i.id === activeTask?.source_item) || null}
						onChange={(_, newValue, reason) => {
							handleCatalogItemAutocompleteChange(reason, newValue, {
								onSelect: (id) => {
									const item = depotItems.find((entry: any) => entry.id === id);
									updateActiveTask(setPendingTasks, {
										source_item: id,
										source_item_desc: item?.ItemName || "",
									});
									onSourceItemChanged(id);
								},
								onClear: () => {
									updateActiveTask(setPendingTasks, {
										source_item: -1,
										source_item_desc: "",
									});
									onSourceItemChanged(-1);
								},
							});
						}}
						loading={isItemLoading}
						onFocus={() => {
							if (items.length === 0) {
								fetchItems();
							}
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
										{t("common:conversion.sourceItem")}:&nbsp;&nbsp;
										<RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
									</span>
								}
								placeholder={isItemLoading ? t("common:ui.placeholders.loadingItems") : t("common:ui.placeholders.selectItem")}
								required
								onFocus={(e) => e.target.select()}
								inputRef={sourceItemRef}
								variant="filled"
								fullWidth
								error={sourceExceedsStock}
								helperText={
									activeTask?.source_item
										? (sourceExceedsStock
											? t("common:conversion.blockers.insufficientStock", {
												required: Util.formatNumber(sourceQty),
												available: Util.formatNumber(sourceStock),
											})
											: isSourceMetered
												? t("common:ui.labels.stock", { value: Util.formatNumber(sourceStock) })
												: "-")
										: undefined
								}
							/>
						)}
						sx={itemAutocompleteSx}
						ListboxProps={{
							style: {
								maxHeight: 400,
							},
						}}
					/>
					</CatalogItemPickerProvider>
				</Grid>
				{barcodeEnabled && (
				<Grid size={{ xs: 4, sm: 4, md: 4 }} >
					<TextField
						id="destBarcodeInput"
						size="small"
						label={t("common:ui.labels.barcode")}
						variant="filled"
						fullWidth
						type="text"
						value={destBarcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={destBarcodeRef}
						slotProps={{
							input: {
								endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupDestBarcode()} />,
							}
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								lookupDestBarcode(destBarcode);
							}
						}}
						onChange={(e) => setDestBarcode(e.target.value)}
						disabled={!activeTask || isItemLoading || Object.keys(barcodes).length === 0}
						error={errorInput === "destBarcodeInput"}
						helperText={
							errorInput === "destBarcodeInput"
								? t("common:stockTrace.barcodeNotFound")
								: ""
						}
					/>
				</Grid>
				)}
				<Grid size={{ xs: barcodeEnabled ? 8 : 12, sm: barcodeEnabled ? 8 : 12, md: barcodeEnabled ? 8 : 12 }} >
					<CatalogItemPickerProvider onItemSaved={handleCatalogSaved}>
					<Autocomplete
						id="destItemInput"
						size="small"
						options={items}
						ListboxComponent={VirtualizedListbox}
						filterOptions={createFilterOptions((opt) => opt.ItemName || "")}
						{...catalogItemAutocompleteInteractionProps}
						getOptionLabel={(item) => item.ItemName || ""}
						isOptionEqualToValue={isCatalogItemOptionEqualToValue}
						renderOption={(props, option) => renderCatalogItemOption(props, option, "both")}
						value={items.find((i) => i.id === activeTask?.dest_item) || null}
						onChange={(_, newValue, reason) => {
							handleCatalogItemAutocompleteChange(reason, newValue, {
								onSelect: (id) => {
									const item = items.find((entry: any) => entry.id === id);
									updateActiveTask(setPendingTasks, {
										dest_item: id,
										dest_item_desc: item?.ItemName || "",
									});
									onDestItemChanged(id);
								},
								onClear: () => {
									updateActiveTask(setPendingTasks, {
										dest_item: -1,
										dest_item_desc: "",
									});
									onDestItemChanged(-1);
								},
							});
						}}
						loading={isItemLoading}
						onFocus={() => {
							if (items.length === 0) {
								fetchItems();
							}
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
										{t("common:conversion.resultItem")}:&nbsp;&nbsp;
										<RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
									</span>
								}
								placeholder={isItemLoading ? t("common:ui.placeholders.loadingItems") : t("common:ui.placeholders.selectItem")}
								required
								onFocus={(e) => e.target.select()}
								inputRef={destItemRef}
								variant="filled"
								fullWidth
								helperText={
									activeTask?.dest_item
										? (destInDepot
											? t("common:ui.labels.stock", { value: Util.formatNumber(destStock) })
											: t("common:catalog.notInDepotHint"))
										: undefined
								}
							/>
						)}
						sx={itemAutocompleteSx}
						ListboxProps={{
							style: {
								maxHeight: 400,
							},
						}}
					/>
					</CatalogItemPickerProvider>
				</Grid>
				<Grid size={12} sx={{ px: 0.5, py: 1 }}>
					<Alert severity={previewReady ? "success" : "info"} variant="outlined" icon={false}>
						<Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
							{previewReady
								? t("common:conversion.preview", {
									sourceQty: Util.formatNumber(sourceQty),
									source: sourceName,
									destQty: Util.formatNumber(produceQty),
									dest: destName,
								})
								: t("common:conversion.previewEmpty")}
						</Typography>
						{previewReady && Number.isFinite(sourceQty) && sourceQty > 0 && (
							<Typography variant="body2" color="text.secondary">
								{t("common:conversion.willTake", {
									qty: Util.formatNumber(sourceQty),
									item: sourceName,
								})}
							</Typography>
						)}
					</Alert>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: activeTask?.convType === "SWAP" ? 4 : 3 }} >
					<QuantityStepperInput
						id="quantityInput"
						size="small"
						label={
							activeTask?.convType === "DE-KIT"
								? t("common:conversion.splitQty")
								: activeTask?.convType === "SWAP"
									? t("common:conversion.swapQty")
									: t("common:conversion.produceQty")
						}
						fullWidth
						required
						disabled={!activeTask}
						value={inputQty > 0 ? inputQty : 1}
						min={qtyMin(inputFractional)}
						max={Math.max(quantityMax, qtyMin(inputFractional))}
						fractional={inputFractional}
						inputRef={quantityRef}
						onFocus={(e) => e.target.select()}
						onChange={(newValue) => {
							if (newValue >= qtyMin(inputFractional)) {
								applyInputQty(newValue);
								validateQty(newValue);
								const next = quantitiesFromInput(activeTask?.convType, newValue, factor);
								setSelectedDestItem((prev: Item | null) => {
									if (!prev) return null;
									return new Item({
										...prev,
										quantity: Number.isFinite(next.produceQty) && next.produceQty > 0 ? next.produceQty : prev.quantity,
									});
								});
								setSelectedSourceItem((prev: Item | null) => {
									if (!prev) return null;
									return new Item({
										...prev,
										quantity: Number.isFinite(next.sourceQty) && next.sourceQty > 0 ? next.sourceQty : prev.quantity,
									});
								});
							}
						}}
						error={errorInput === "quantityInput" || sourceExceedsStock}
						helperText={
							sourceExceedsStock
								? t("common:conversion.blockers.insufficientStock", {
									required: Util.formatNumber(sourceQty),
									available: Util.formatNumber(sourceStock),
								})
								: errorInput === "quantityInput"
									? errorMessageRef.current
									: undefined
						}
					/>
				</Grid>
				{activeTask?.convType !== "SWAP" && (
				<Grid size={{ xs: 12, sm: 6, md: 3 }} >
					<TextField
						id="factorInput"
						size="small"
						label={t("common:conversion.factorLabel")}
						variant="filled"
						fullWidth
						type="number"
						value={activeTask?.factor ?? 1}
						required
						onFocus={(e) => e.target.select()}
						onChange={(e) => {
							const newValue = parseInt(e.target.value);
							if (validateFctr(newValue)) {
								applyInputQty(inputQty, newValue);
							}
						}}
						error={errorInput === "factorInput"}
						helperText={
							errorInput === "factorInput"
								? errorMessageRef.current
								: activeTask?.convType === "DE-KIT"
									? t("common:conversion.factorHelpDekit")
									: t("common:conversion.factorHelpKit")
						}
					/>
				</Grid>
				)}
				<Grid size={{ xs: 12, sm: 6, md: activeTask?.convType === "SWAP" ? 4 : 3 }} >
					<AsynchronousPrice
						fetchPrices={fetchSourcePrices}
						prices={sourcePrices}
						selectedItem={selectedSourceItem}
						setSelectedItem={setSelectedSourceItem}
						itemConstructor={Item}
						isPriceLoading={isSourcePriceLoading}
						label={t("common:conversion.sourceCost")}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: activeTask?.convType === "SWAP" ? 4 : 3 }} >
					<AsynchronousPrice
						fetchPrices={fetchDestPrices}
						prices={destPrices}
						selectedItem={selectedDestItem}
						setSelectedItem={setSelectedDestItem}
						itemConstructor={Item}
						isPriceLoading={isDestPriceLoading}
						label={t("common:conversion.destCost")}
					/>
				</Grid>
			</Grid>
		</form>
	);
};

export default ConversionItemDetail;
